import { defaultToAny, defaultTo, get } from './Utility';
import { registry } from './Registry';
import { TokenType } from './Types';
import { createIntegratorChild } from '@nsftx/seven-client-sdk-integrator';
import * as i18nIsoLanguages from '@cospired/i18n-iso-languages';
export class IgniteAdapter {
    constructor(options) {
        this.options = options;
    }
    getConfiguration(igniteData) {
        if (!igniteData)
            return;
        if (createIntegratorChild) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.ignite = new createIntegratorChild({
                context: {
                    env: igniteData.environment || 'staging',
                    debug: true,
                    allowedOrigins: this.options.allowedOrigins,
                },
                app: {
                    id: crypto.randomUUID(),
                    version: igniteData.version,
                    displayId: igniteData.displayId,
                    providerId: igniteData.providerId,
                },
                events: {
                    onLoad: this.onLoad.bind(this),
                    onSettingsUpdated: (data) => {
                        var _a, _b;
                        this.settingsUpdate = data;
                        if (((_a = data.activeLocale) === null || _a === void 0 ? void 0 : _a.length) === 3) {
                            const lang = (_b = i18nIsoLanguages.alpha3BToAlpha2(data.activeLocale)) !== null && _b !== void 0 ? _b : 'en';
                            data.activeLocale = lang === 'sr' ? 'sr-Latn' : lang;
                        }
                        if (data.oddsOption) {
                            data.oddsOption = this.getOdds(data.oddsOption);
                        }
                        this.parseAndDispatchEvents('onSettingsUpdated', data);
                    },
                    onPunterDetailsUpdated: (data) => {
                        this.user = Object.assign(Object.assign({}, this.user), data);
                        this.parseAndDispatchEvents('onPunterDetailsUpdated', data);
                    },
                    onPunterSessionUpdated: (data) => {
                        this.user.token = data.punterToken;
                        this.parseAndDispatchEvents('onPunterSessionUpdated', data);
                        this.sendAuthorizationChangeEvent(this.parentOnLoadMessage);
                    },
                    onPunterSessionDestroyed: () => {
                        this.user = null;
                        this.parseAndDispatchEvents('onPunterSessionDestroyed');
                        this.sendAuthorizationChangeEvent(this.parentOnLoadMessage);
                    },
                    onPunterWalletsUpdated: (data) => {
                        this.user.balance = data.activeCurrencyBalance;
                        this.parseAndDispatchEvents('User.BalanceChanged', { 'balance': data.activeCurrencyBalance });
                    },
                    onCancelBetsRequested: (data) => {
                        this.parseAndDispatchEvents('onCancelBetsRequested', data);
                    },
                    onReBetRequested: (data) => {
                        this.parseAndDispatchEvents('onReBetRequested', data);
                    },
                    onUISnoozed: () => {
                        this.parseAndDispatchEvents('onUISnoozed');
                    },
                    onUIAwakened: () => {
                        this.parseAndDispatchEvents('onUIAwakened');
                    },
                    onBettingDenied: () => {
                        this.parseAndDispatchEvents('onBettingDenied');
                    },
                    onBettingAllowed: () => {
                        this.parseAndDispatchEvents('onBettingAllowed');
                    },
                    onNavigationChanged: (data) => {
                        this.parseAndDispatchEvents('onNavigationChanged', data);
                    },
                    onNavigationRequested: (data) => {
                        this.parseAndDispatchEvents('onNavigationRequested', data);
                    },
                    onGameOptionsUpdated: (data) => {
                        this.parseAndDispatchEvents('onGameOptionsUpdated', data);
                    },
                    onDomEvent: (data) => {
                        this.parseAndDispatchEvents('onDomEvent', data);
                    },
                }
            });
            Object.assign(registry.messageProtocols.ignite, {
                handler: this.ignite,
            });
        }
        this.setWebView();
    }
    getUserLocale(user) {
        if (user) {
            if (user.details) {
                return user.details.language;
            }
            return user.language;
        }
        return undefined;
    }
    getUserTimezone(user) {
        if (user) {
            if (user.details) {
                return user.details.timezone;
            }
            return user.timezone;
        }
        return undefined;
    }
    getCompanyProductId(company) {
        if (company === null || company === void 0 ? void 0 : company.config) {
            return company.config.cpvUuid;
        }
        return undefined;
    }
    getCompanyTimezoneOffset(company) {
        if (company === null || company === void 0 ? void 0 : company.config) {
            return company.config.timezoneOffset;
        }
        return 0;
    }
    parseUser(user, balance) {
        if (user) {
            return {
                id: user.id || user.identity.id,
                username: user.nickname || user.details.nickname,
                email: user.email,
                firstName: '',
                lastName: '',
                balance: user.balance || +balance || 0,
                token: user.token || user.playerToken.value,
                tokenType: TokenType.Bearer,
                timezone: '',
            };
        }
        return user;
    }
    setWebView() {
        const webViewHandler = (action, payload) => {
            const cleanMessage = JSON.stringify(payload);
            const message = JSON.parse(cleanMessage);
            this.onLoad(message);
        };
        Object.assign(window, {
            onParentMessage: webViewHandler,
        });
    }
    onLoad(message) {
        this.parentOnLoadMessage = Object.assign({}, message);
        const payload = message;
        const betslip = defaultTo(get(payload, 'betslip'), undefined);
        const context = defaultTo(get(payload, 'context'), undefined);
        const settings = defaultTo(get(payload, 'settings'), undefined);
        const company = defaultToAny(get(payload, 'company'), get(payload, 'tenant'), {});
        const product = defaultToAny(get(payload, 'product'), get(payload, 'account'), {});
        const user = defaultTo(get(payload, 'punter'), undefined);
        const balance = defaultTo(get(payload, 'activeCurrencyBalance'), '');
        const channel = defaultTo(get(payload, 'dp'), 'web');
        const betshop = defaultTo(get(payload, 'betshop'), {});
        const device = defaultToAny(get(payload, 'device'), betshop.device, {});
        const integration = defaultTo(get(payload, 'integration'), undefined);
        const language = defaultTo(get(settings, 'activeLocale'), {});
        const scrollButtons = defaultTo(get(settings, 'scrollButtons'), undefined);
        const moneyDenominations = defaultTo(get(settings, 'moneyDenominations'), undefined);
        const environment = defaultToAny(context.environment, context.env, undefined);
        const platformName = registry.platforms.NextGen.name;
        const platformDataCenter = defaultToAny(payload.platformDataCenter, payload.platformDc, undefined);
        const productName = defaultToAny(product.id, undefined);
        const productId = defaultToAny(product.cpvUuid, product.linkId, this.getCompanyProductId(company), undefined);
        const productUuid = defaultToAny(product.uuid, undefined);
        const tenantId = defaultToAny(context.uuid, context.tenantId, undefined);
        const locale = defaultToAny(language.iso1, payload.language, company.language, this.getUserLocale(user), undefined);
        const applicationName = defaultToAny(payload.applicationName, payload.appName, payload.app, 'web');
        const channelId = defaultToAny(channel, undefined);
        const betshopId = defaultToAny(betshop.uuid, undefined);
        const deviceId = defaultToAny(device.uuid, undefined);
        const isIntegration = defaultToAny(integration, undefined);
        const event = new CustomEvent('Configuration:SetConfiguration', {
            detail: {
                environment,
                platformName,
                platformDataCenter,
                productName,
                productId,
                productUuid,
                tenantId,
                locale,
                applicationName,
                channelId,
                betshopId,
                deviceId,
                isIntegration,
                betslip,
                scrollButtons,
                moneyDenominations,
                settings,
                user: this.parseUser(user, balance),
                ignite: this.ignite,
            },
        });
        window.dispatchEvent(event);
    }
    parseAndDispatchEvents(action, data = null) {
        const prepareMessage = {
            data: {
                msgSender: 'Master',
                action,
                data,
            },
        };
        window.dispatchEvent(new MessageEvent('message', prepareMessage));
    }
    getOdds(odd) {
        switch (odd) {
            case 'FRACTIONAL':
                return 2;
            case 'AMERICAN':
                return 3;
            default:
                return 1;
        }
    }
    sendAuthorizationChangeEvent(data = null) {
        var _a, _b, _c, _d, _e;
        const userRaw = this.user;
        const balance = (_b = (_a = this.user) === null || _a === void 0 ? void 0 : _a.balance) !== null && _b !== void 0 ? _b : data.activeCurrencyBalance;
        const authorizationData = {
            'auth': {
                'token': null,
                'tpToken': null,
                'user': {
                    'name': null,
                    'id': null,
                    'email': null,
                    'firstName': null,
                    'lastName': null,
                    'nickname': 'Guest',
                    'logged': false,
                    'profile': null,
                    'registrationOrigin': null,
                    'balance': 0,
                    'balanceTotal': 0,
                    'settings': {
                        'paymentMethod': 'VirtualMoney',
                        'showBalance': false,
                        'oddType': ((_c = this.settingsUpdate) === null || _c === void 0 ? void 0 : _c.oddsOption) || 1,
                        'forceHideBalance': false
                    },
                    'currency': 'BAM',
                    'currencyVirtual': false,
                    'bonus': {
                        'balance': 0,
                        'balanceSmallUnit': 0,
                        'active': false,
                        'products': [],
                        'visible': false
                    },
                    'token': null,
                    'type': null,
                }
            }
        };
        if (userRaw) {
            const user = this.parseUser(userRaw, balance);
            const newUser = Object.assign(Object.assign({}, authorizationData.auth.user), user);
            newUser.balanceTotal = newUser.balance;
            newUser.type = userRaw.type;
            newUser.nickname = userRaw.nickname;
            newUser.logged = !!newUser.token;
            newUser.currency = ((_d = this.settingsUpdate) === null || _d === void 0 ? void 0 : _d.activeCurrency.code) || data.settings.activeCurrency.code;
            newUser.settings.oddType = ((_e = this.settingsUpdate) === null || _e === void 0 ? void 0 : _e.oddsOption) || this.getOdds(data.settings.oddsOption);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            authorizationData.auth.token = newUser.token;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            authorizationData.auth.user = newUser;
        }
        this.parseAndDispatchEvents('User.AuthorizationChanged', authorizationData);
    }
}
//# sourceMappingURL=IgniteAdapter.js.map